import React from 'react';
import { Coordinate, VideoOverlayObject, Video } from '../VideoPlayer/VideoTypes';
import { Flag, FlagStatus } from '../Sessions/Label/useValidationFlags';
import { PositionBuffer, FrameRange } from '../Sessions/Label/SessionLabelTask';
import { withinBoundaries } from './Trail';

type Props = {
  flags: Flag[];
  buffers: PositionBuffer[];
  excludeRanges: FrameRange[];
  video: Video;
  primaryColor: string;
  secondaryColor: string;
  maxX?: number;
  maxY?: number;
};

const FlagTrails = (props: Props) => {
  const { 
    flags,
    buffers,
    excludeRanges,
    video,
    primaryColor,
    secondaryColor,
  } = props;

  const paths: JSX.Element[] = [];
  let centerCoord: Coordinate | undefined = undefined;

  let j: number = 0;
  // Flag trail
  flags
    .filter((flag: Flag) => flag.status === FlagStatus.Unresolved)
    .filter((flag: Flag) => {
      const notInExclusionRange: boolean = excludeRanges.find((range: FrameRange) => 
        (range.start_frame >= flag.start && range.start_frame <= flag.end)
        || (range.end_frame && (range.end_frame >= flag.start && range.end_frame <= flag.end))
      ) === undefined;
      return notInExclusionRange;
    })
    .forEach((flag: Flag) => {
      const flagTrailCoords: Coordinate[] = [];
      buffers.forEach((buffer: PositionBuffer) => {
        // No flag overlap with buffer? Skip
        if(buffer.page_start > flag.end || buffer.page_start+buffer.page_size < flag.start) {
          return;
        }
        // Get the positions we want from this buffer and add it to our trail
        const flagCoords: Coordinate[] = [...buffer.video_coordinates?.get(String(video.id))] || [];
        // Flag ends earlier than buffer, so don't use all coords until the end
        if(buffer.page_start+buffer.page_size >= flag.end) {
          flagCoords.splice(flag.end-buffer.page_start, buffer.page_start+buffer.page_size-flag.end)
        }
        if(buffer.page_start < flag.start) {
          flagCoords.splice(0, flag.start-buffer.page_start);
        }
        flagTrailCoords.push(...flagCoords);
      });

    let trailPaths: string[] = [""];
    flagTrailCoords.forEach((coord: Coordinate) => {
      if(!withinBoundaries(coord, video.width || undefined, video.height || undefined)) {
        // we skip if it's not within our boundaries, and start a new trail (if we didnt already)
        if(trailPaths[trailPaths.length-1].length > 0) {
          trailPaths.push("");
        }
      } else {
        // set our center coord if we havent yet (within boundaries)
        if(!centerCoord) {
          centerCoord = coord;
        }

        const index: number = trailPaths.length-1;
        let currentPath: string = trailPaths[index];
        const isFirst: boolean = currentPath.length === 0;
        currentPath += (isFirst ? "M " : "L ") + coord.x.toString() + " " + coord.y.toString() + " ";
        trailPaths[index] = currentPath;
      }
    });

    // Create svg
    let i: number = 0;
    const trailSvg = <svg key={"j"+j++}>
      {
        trailPaths.map((path: string) => {
          return <React.Fragment key={"i"+i++}>
            <path 
              fill="none"
              stroke={primaryColor}
              strokeWidth={2}
              d={path}
            />
            <path 
              fill="none"
              stroke={secondaryColor}
              strokeDasharray="7"
              strokeWidth={2}
              d={path}
            />
          </React.Fragment>
        })
      }
    </svg>
    paths.push(trailSvg);
  });

  if(!centerCoord) return undefined;

  const trailsSvg = <svg>
    {paths}
  </svg>;

  const overlayObject : VideoOverlayObject = {
    svg: trailsSvg,
    width: 0,
    height: 0,
    center: centerCoord,
    disableAutoScale: true,
  };

  return overlayObject;
}

export default FlagTrails;